class SoulSpaceBlendSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.successTimers = new WeakMap();
    this.triggers = Array.from(this.querySelectorAll('[data-blend-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-blend-panel][data-blend-index]'));
    this.addEventListener('submit', (event) => this.onCartSubmit(event));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.select(Number(trigger.dataset.blendTrigger)));
      trigger.addEventListener('keydown', (event) => this.onKeydown(event));
    });

    if (this.triggers.length) {
      const currentProductId = this.dataset.currentProductId;
      const currentIndex = this.triggers.findIndex((trigger) => trigger.dataset.productId === currentProductId);
      this.select(currentIndex >= 0 ? currentIndex : 0);
    }
  }

  select(index, focus = false) {
    let activeTrigger;
    this.triggers.forEach((trigger, triggerIndex) => {
      const active = Number(trigger.dataset.blendTrigger) === index;
      trigger.setAttribute('aria-pressed', String(active));
      trigger.tabIndex = active ? 0 : -1;
      if (active) activeTrigger = trigger;
      if (active && focus) trigger.focus({ preventScroll: true });
    });
    this.panels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.blendIndex) !== index;
    });
    if (activeTrigger) this.scrollSwatchIntoView(activeTrigger);
  }

  scrollSwatchIntoView(trigger) {
    const scroller = trigger.closest('.soul-blends__swatches');
    if (!scroller || this.classList.contains('soul-blends--compact')) return;
    const left = trigger.offsetLeft - (scroller.clientWidth - trigger.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }

  onKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = this.triggers.indexOf(event.currentTarget);
    let nextPosition = event.key === 'Home' ? 0 : event.key === 'End' ? this.triggers.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
    nextPosition = (nextPosition + this.triggers.length) % this.triggers.length;
    this.select(Number(this.triggers[nextPosition].dataset.blendTrigger), true);
  }

  async onCartSubmit(event) {
    const form = event.target.closest('form[data-soul-ajax-cart]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const panel = form.closest('[data-blend-panel]');
    const status = panel?.querySelector('[data-soul-cart-status]');
    const formData = new FormData(form);
    if (!formData.get('id') || button?.disabled) return;

    const previousTimer = button ? this.successTimers.get(button) : null;
    if (previousTimer) {
      window.clearTimeout(previousTimer.id);
      button.textContent = previousTimer.label;
      this.successTimers.delete(button);
    }
    const originalLabel = button?.textContent;
    const wasDisabled = button?.disabled ?? false;
    const previousAriaBusy = button?.getAttribute('aria-busy');

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (status) {
      status.textContent = '';
      status.classList.add('visually-hidden');
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this blend.');

      if (status) status.textContent = 'Added to cart.';
      if (button) {
        button.textContent = 'Added to cart';
        const timer = window.setTimeout(() => {
          button.textContent = originalLabel;
          this.successTimers.delete(button);
        }, 2000);
        this.successTimers.set(button, { id: timer, label: originalLabel });
      }

      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(`Cart refresh failed with status ${cartResponse.status}.`);
        this.updateCartFeedback(await cartResponse.json());
      } catch (error) {
        console.warn('[soul-space-blend-selector] Added to cart, but the cart count could not be refreshed:', error);
      }
    } catch (error) {
      if (status) {
        status.classList.remove('visually-hidden');
        status.textContent = error instanceof Error ? error.message : 'Unable to add this blend.';
      }
    } finally {
      if (button) {
        button.disabled = wasDisabled;
        if (previousAriaBusy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', previousAriaBusy);
      }
    }
  }

  updateCartFeedback(cart) {
    const count = Number(cart?.item_count) || 0;
    const cartIcons = Array.from(document.querySelectorAll('cart-icon'));
    const renderers = cartIcons.filter((icon) => typeof icon.renderCartBubble === 'function');
    if (renderers.length) {
      renderers.forEach((icon) => icon.renderCartBubble(count, true));
      return;
    }

    const displayCount = count > 99 ? '99+' : String(count);
    const countSelectors = [
      '#cart-bubble-text .cart-bubble__text-count',
      '[data-testid="cart-bubble"]',
      '.cart-bubble__text-count',
      '[data-cart-count]',
      '.cart-count',
      '.cart__count',
      '[class*="cart-count"]',
    ];

    document.querySelectorAll(countSelectors.join(',')).forEach((element) => {
      element.textContent = displayCount;
      element.classList.toggle('hidden', count === 0);
      element.classList.toggle('visually-hidden', count === 0);
      element.removeAttribute('hidden');
    });

    document.querySelectorAll('.cart-bubble, #cart-bubble-text, [data-testid="cart-bubble"]').forEach((element) => {
      element.classList.toggle('hidden', count === 0);
      element.classList.toggle('visually-hidden', count === 0);
      if (count > 0) element.removeAttribute('hidden');
    });

    const cartTargets = document.querySelectorAll("cart-icon, header-cart, a[href*='/cart'], [data-testid='cart-drawer-trigger']");
    cartTargets.forEach((element) => {
      element.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      element.classList.remove('soul-cart-feedback-pulse');
      void element.offsetWidth;
      element.classList.add('soul-cart-feedback-pulse');
      window.setTimeout(() => element.classList.remove('soul-cart-feedback-pulse'), 400);
    });

    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.toggleAttribute('data-maintain-ratio', count <= 99);
      bubble.classList.remove('cart-bubble--animating');
      void bubble.offsetWidth;
      bubble.classList.add('cart-bubble--animating');
      window.setTimeout(() => bubble.classList.remove('cart-bubble--animating'), 400);
    });
  }

}

if (!customElements.get('soul-space-blend-selector')) {
  customElements.define('soul-space-blend-selector', SoulSpaceBlendSelector);
}
